import React, { useState } from "react";
import { color as d3Color } from "d3";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import store from "../../../../state/store";
import GeneHistogram from "./GeneHistogram";
import {
  pathogenicStrongColor,
  pathogenicModerateColor,
  uncertainColor,
  benignStrongColor,
  benignModerateColor,
  chineseSilver,
  blackColor,
  gainsboro,
  whisper,
} from "../../../../common/helpers/colors";
import {
  cropNumber,
  getAlleleFrequency,
  areEqual,
} from "../../../../common/helpers/helpers";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import HelperText from "../../../../components/atoms/HelperText";
import HelperTextLabel from "../../../../components/atoms/HelperText/HelperTextLabel";

const COLORS = {
  0: pathogenicStrongColor,
  1: pathogenicModerateColor,
  2: uncertainColor,
  3: benignModerateColor,
  4: benignStrongColor,
};

const { string, number, shape, arrayOf } = PropTypes;

const prepareHistogramData = (frequencyHist) => {
  const data = [];
  const { buckets } = frequencyHist;

  buckets.forEach((item) => {
    data.push({
      x_axis: cropNumber(item.to, 3),
      benign: item.benign,
      uncertain: item.uncertain,
      pathogenic: item.pathogenic,
      from: cropNumber(item.from, 3),
      to: cropNumber(item.to, 3),
    });
  });

  return {
    data,
    total_pathogenic: frequencyHist.pathogenic,
    total_uncertain: frequencyHist.uncertain,
    total_benign: frequencyHist.benign,
  };
};

const SaphetorKnownPathogenicity = ({ data, gene }) => {
  const [pathogenicityFilters] = useState([]);
  const [impactFilters] = useState([]);
  const [percent, setPercent] = useState(false);

  const getArraySum = (arr) => {
    if (!arr) {
      return null;
    }
    return arr.reduce((a, b) => a + b, 0);
  };

  const getColoredCell = (
    item,
    value,
    variantClass,
    impact,
    pathogenicity,
    localPercent = false
  ) => {
    const sum = getArraySum(item);
    let res = sum ? (value / sum) * 100 : 0;
    const isActive =
      (impactFilters.length > 0 &&
        pathogenicityFilters.length === 0 &&
        impactFilters.indexOf(impact) !== -1) ||
      (impactFilters.length === 0 &&
        pathogenicityFilters.length > 0 &&
        pathogenicityFilters.indexOf(pathogenicity) !== -1) ||
      (impactFilters.length > 0 &&
        pathogenicityFilters.length > 0 &&
        impactFilters.indexOf(impact) !== -1 &&
        pathogenicityFilters.indexOf(pathogenicity) !== -1);

    const isInactive =
      (impactFilters.length > 0 && impactFilters.indexOf(impact) === -1) ||
      (pathogenicityFilters.length > 0 &&
        pathogenicityFilters.indexOf(pathogenicity) === -1);
    const style = {
      backgroundColor: d3Color(COLORS[variantClass])
        .copy({ opacity: res / (isInactive ? 300 : 100) })
        .formatHex8(),
    };
    // Zeroes should be muted
    if (!res) {
      style.color = chineseSilver;
    }
    if (isInactive) {
      style.color = chineseSilver;
    }
    if (isActive) {
      style.color = blackColor;
      style.fontWeight = "bold";
    }
    // Display absolute number
    if (!localPercent) {
      res = value;
    }
    return (
      <td style={style}>{localPercent ? `${cropNumber(res, 1)}%` : res}</td>
    );
  };

  const getPercentageOrNumber = (item, idx, impact, pathogenicity) => {
    if (!item) {
      return null;
    }
    return getColoredCell(item, item[idx], idx, impact, pathogenicity, percent);
  };

  const renderImpactRow = (item, impact, label) => {
    if (getArraySum(item[impact]) > 0) {
      const style = {
        textAlign: "left",
        fontWeight: "bold",
      };
      const isActive = impactFilters.indexOf(impact) !== -1;
      if (isActive) {
        style.backgroundColor = gainsboro;
        style.color = blackColor;
      }
      return (
        <tr>
          <td onClick={() => {}} style={style}>
            {label} {isActive && <i className="fa fa-filter" />}{" "}
            <HelperText fieldDescriptionKey={label} />
          </td>
          {getPercentageOrNumber(item[impact], 0, impact, "pathogenic")}
          {getPercentageOrNumber(item[impact], 1, impact, "likely_pathogenic")}
          {getPercentageOrNumber(
            item[impact],
            2,
            impact,
            "uncertain_significance"
          )}
          {getPercentageOrNumber(item[impact], 3, impact, "likely_benign")}
          {getPercentageOrNumber(item[impact], 4, impact, "benign")}
          <td className="varsome-cell-total">{getArraySum(item[impact])}</td>
        </tr>
      );
    }
    return null;
  };

  const renderPathogenicityHeader = (pathogenicity, label) => {
    const style = {
      textAlign: "center",
      fontWeight: "bold",
    };
    const isActive = pathogenicityFilters.indexOf(pathogenicity) !== -1;
    if (isActive) {
      style.backgroundColor = whisper;
    }

    return (
      <th onClick={() => {}} style={style}>
        {label}{" "}
        {pathogenicityFilters.indexOf(pathogenicity) !== -1 && (
          <i className="fa fa-filter" />
        )}
      </th>
    );
  };

  if (data && Array.isArray(data.items)) {
    return (
      <div className="varsome-result">
        <div className="varsome-container varsome-container__basic">
          <ComponentPanel
            title={
              <>
                <b>Variants in {gene} with known pathogenicity</b>
                <HelperText fieldDescriptionKey="Gene statistics" />
              </>
            }
            componentName="SaphetorKnownPathogenicity"
            version={data.version}
            content={
              <div className="varsome-inner-container">
                <div className="varsome-subcontainer">
                  {data.items.map((item, i) => {
                    const variantUrl = `${
                      store.getState().varsomeHost
                    }/variant/${store.getState().genome.name}/${
                      item.most_common_pathogenic_variant
                    }`;
                    let pVariantText;
                    let bcfVariantText;
                    let cutOffFreq;
                    let histogramData = {};
                    if (item.frequency_histogram)
                      histogramData = prepareHistogramData(
                        item.frequency_histogram
                      );
                    if (
                      item.most_common_pathogenic_variant &&
                      item.most_common_pathogenic_variant_ethnicity &&
                      item.most_common_pathogenic_variant_frequency
                    ) {
                      pVariantText = (
                        <span>
                          The pathogenic variant with the highest allele
                          frequency is:{" "}
                          <a href={variantUrl} target="_blank">
                            {item.most_common_pathogenic_variant}
                          </a>
                          , with {item.most_common_pathogenic_variant_ethnicity}{" "}
                          frequency ={" "}
                          {getAlleleFrequency(
                            item.most_common_pathogenic_variant_frequency
                          )}{" "}
                          .
                        </span>
                      );
                    }
                    if (item.frequency_benign_threshold) {
                      cutOffFreq = item.frequency_benign_threshold;
                      bcfVariantText = (
                        <div>
                          The recommended benign frequency cut-off for rule BS1
                          is{" "}
                          <b>
                            {cropNumber(item.frequency_benign_threshold, 2)}
                          </b>
                          , derived from the {item.total_variants} clinical
                          known variants observed in gene {item.gene_symbol}, of
                          which {item.pathogenic} pathogenic, {item.uncertain}{" "}
                          uncertain and {item.benign} benign.
                        </div>
                      );
                    }

                    return (
                      <div key={i}>
                        <div className="varsome-horiz-flex">
                          <div className="varsome-horiz-list-item">
                            <HelperTextLabel
                              label="Total classified variants (UniProt, ClinVar, LOVD, MitoMap, VarSome & PubMed)"
                              fieldDescriptionKey="Total classified variants (UniProt, ClinVar, LOVD, MitoMap, VarSome & PubMed)"
                            />
                            <div className="varsome-horiz-list-value">
                              {item.total_variants}
                            </div>
                          </div>
                        </div>
                        <div className="varsome-horiz-flex">
                          <div className="varsome-horiz-list-item">
                            <HelperTextLabel
                              label="Pathogenic"
                              fieldDescriptionKey="Pathogenic"
                            />

                            <div className="varsome-horiz-list-value">
                              {item.pathogenic}
                            </div>
                          </div>
                          <div className="varsome-horiz-list-item">
                            <HelperTextLabel
                              label="Uncertain significance"
                              fieldDescriptionKey="Uncertain significance"
                            />
                            <div className="varsome-horiz-list-value">
                              {item.uncertain}
                            </div>
                          </div>
                          <div className="varsome-horiz-list-item">
                            <HelperTextLabel
                              label="Benign"
                              fieldDescriptionKey="Benign"
                            />
                            <div className="varsome-horiz-list-value">
                              {item.benign}
                            </div>
                          </div>
                        </div>
                        <br />
                        <table className="varsome-table varsome-gene-stats-table">
                          <thead>
                            <tr>
                              <th>Coding impact</th>
                              {renderPathogenicityHeader(
                                "pathogenic",
                                "Pathogenic"
                              )}
                              {renderPathogenicityHeader(
                                "likely_pathogenic",
                                "Likely Pathogenic"
                              )}
                              {renderPathogenicityHeader(
                                "uncertain_significance",
                                "Uncertain Significance"
                              )}
                              {renderPathogenicityHeader(
                                "likely_benign",
                                "Likely Benign"
                              )}
                              {renderPathogenicityHeader("benign", "Benign")}
                              <th className="varsome-cell-total tw-text-center">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody className="varsome-text-center">
                            {renderImpactRow(item, "synonymous", "Synonymous")}
                            {renderImpactRow(item, "missense", "Missense")}
                            {renderImpactRow(item, "nonsense", "Nonsense")}
                            {renderImpactRow(item, "start_loss", "Start loss")}
                            {renderImpactRow(item, "stoploss", "Stoploss")}
                            {renderImpactRow(item, "frameshift", "Frameshift")}
                            {renderImpactRow(item, "in_frame", "Inframe Indel")}
                            {renderImpactRow(
                              item,
                              "exon_deletion",
                              "Exon deletion"
                            )}
                            {renderImpactRow(
                              item,
                              "splice_junction_loss",
                              "Splice junction loss"
                            )}
                            {renderImpactRow(item, "non_coding", "Non-coding")}
                            <tr className="varsome-table-totals">
                              <td className="tw-text-left tw-font-bold">
                                Total <HelperText fieldDescriptionKey="Total" />
                              </td>
                              {getPercentageOrNumber(item.totals, 0)}
                              {getPercentageOrNumber(item.totals, 1)}
                              {getPercentageOrNumber(item.totals, 2)}
                              {getPercentageOrNumber(item.totals, 3)}
                              {getPercentageOrNumber(item.totals, 4)}
                              <td className="varsome-cell-total">
                                {getArraySum(item.totals)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div className="clearfix">
                          <div className="varsome-right btn">
                            <label
                              className="tw-cursor-pointer"
                              htmlFor="display_percent"
                              style={{ fontSize: "0.8rem" }}
                            >
                              <input
                                id="display_percent"
                                type="checkbox"
                                value={percent}
                                onChange={() => setPercent(!percent)}
                              />{" "}
                              Display percentages
                            </label>
                          </div>
                        </div>
                        <GeneHistogram
                          data={histogramData.data}
                          cutOffFreq={cutOffFreq}
                          total_pathogenic={histogramData.total_pathogenic}
                          total_uncertain={histogramData.total_uncertain}
                          total_benign={histogramData.total_benign}
                          uniqueKey={`key_${i}`}
                        />
                        <div className="varsome-horiz-flex">
                          {bcfVariantText}
                        </div>
                        <div className="varsome-horiz-flex">{pVariantText}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            }
          />
        </div>
      </div>
    );
  }
  return null;
};

SaphetorKnownPathogenicity.propTypes = {
  gene: string,
  data: shape({
    items: arrayOf(
      shape({
        benign: number,
        exon_deletion: arrayOf(number),
        frameshift: arrayOf(number),
        frequency_benign_threshold: number,
        frequency_histogram: shape({
          benign: number,
          buckets: arrayOf(
            shape({
              benign: number,
              from: number,
              pathogenic: number,
              to: number,
              uncertain: number,
            })
          ),
          pathogenic: number,
          uncertain: number,
        }),
        frequency_pathogenic_total: number,
        gene_id: number,
        gene_symbol: string,
        in_frame: arrayOf(number),
        missense: arrayOf(number),
        most_common_pathogenic_variant: string,
        most_common_pathogenic_variant_ethnicity: string,
        most_common_pathogenic_variant_frequency: number,
        non_coding: arrayOf(number),
        nonsense: arrayOf(number),
        pathogenic: number,
        splice_junction_loss: arrayOf(number),
        start_loss: arrayOf(number),
        stoploss: arrayOf(number),
        synonymous: arrayOf(number),
        total_frequencies: number,
        total_variants: number,
        totals: arrayOf(number),
        uncertain: number,
      })
    ),
    version: string,
  }),
};

SaphetorKnownPathogenicity.defaultProps = {
  gene: "",
  data: null,
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(
  withErrorGuard(
    React.memo(SaphetorKnownPathogenicity, areEqual),
    "SaphetorKnownPathogenicity",
    "Variants in this gene with known pathogenicity"
  )
);
